import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { createStyles, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from "react-router";
import { INavigations, mapNavigationsToProps, ContactpointNavigation, ContactpointNavigationPage } from '../../organisms/contactpoint/ContactpointNavigation';
import { IReportDataObject } from '../../../entities/reports/IReportData';
import { IData as IContactpointData } from "../../../store/contactpoint/ContactpointItem";
import { FetchState } from '../../../entities/FetchState';
import { PageHeader } from '../../organisms/PageHeader';
import { ContactpointForm } from '../../organisms/contactpoint/ContactpointForm';
import { SaveButton } from '../../atoms/buttons/SaveButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import { EnableButton } from '../../atoms/buttons/EnableButton';
import { DisableButton } from '../../atoms/buttons/DisableButton';
import ErrorMessage from '../../organisms/ErrorMessage';
import { SavingMessage } from '../../organisms/SavingMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';
import { EnablingMessage } from '../../organisms/EnablingMessage';
import { DisablingMessage } from '../../organisms/DisablingMessage';
import { UnsavedChangesBar } from '../../organisms/UnsavedChangesBar';

// CSS
const useStyles = makeStyles((theme: Theme) =>
  createStyles({}),
);

// Interface for Route Parameters
interface IMatchParams {
  id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch { }

// Properties from State 
interface IStateProps {
  details: IReportDataObject<IContactpointData>
  updater: IReportDataObject<IContactpointData>
  deleter: IReportDataObject<IContactpointData>
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
  details: state.contactpoint.contactpointItem.Item,
  updater: state.contactpoint.contactpointItem.UpdateItem,
  deleter: state.contactpoint.contactpointItem.DeleteItem
});

// Outgoing functions to Dispatch
interface IDispatchProps {
  navigations: INavigations;
  onLoadDetail: (id: string) => void;
  onEdit: (details: IContactpointData) => void;
  onDelete: (id: string) => void;
  onEnable: (id: string) => void;
  onDisable: (id: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
  navigations: mapNavigationsToProps(dispatch),
  onLoadDetail: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.FetchData(id)),
  onEdit: (details: IContactpointData) => dispatch(RootActionCreators.Contactpoint.contactpointItem.UpdateData(details)),
  onDelete: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.DeleteData(id)),
  onEnable: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.EnableData(id)),
  onDisable: (id: string) => dispatch(RootActionCreators.Contactpoint.contactpointItem.DisableData(id))
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const ContactpointEdit = (props: AllProps) => {
  const classes = useStyles(useTheme());
  const { t: tContactpoint } = useTranslation('contactpoint');
  const { t: tCommon } = useTranslation('common');

  const isError = props.details.state === FetchState.Error;
  const isLoading = props.details.state === FetchState.NotFetched || props.details.state === FetchState.Busy || isError;
  const isDeleting = props.deleter.state === FetchState.Busy;
  const hasDeleteError = props.deleter.state === FetchState.Error;
  
  // Keep track of the current operation type
  const [currentOperation, setCurrentOperation] = React.useState<'enable' | 'disable' | 'none'>('none');
  
  // Determine if enabling or disabling based on operation type and busy state
  const isEnabling = props.details.state === FetchState.Busy && currentOperation === 'enable';
  const isDisabling = props.details.state === FetchState.Busy && currentOperation === 'disable';

  const [details, setDetails] = React.useState(props.details.data);
  const [detailsValid, setDetailsValid] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  // Track the operation type when buttons are clicked
  const handleEnable = (id: string) => {
    setCurrentOperation('enable');
    props.onEnable(id);
  };

  const handleDisable = (id: string) => {
    setCurrentOperation('disable');
    props.onDisable(id);
  };
  
  // Reset operation type when operation completes
  React.useEffect(() => {
    if (props.details.state !== FetchState.Busy) {
      setCurrentOperation('none');
    }
  }, [props.details.state]);

  // Reset unsaved changes flag after successful save
  React.useEffect(() => {
    if (props.updater.state === FetchState.Success) {
      setHasUnsavedChanges(false);
    }
  }, [props.updater.state]);

  // Load data when component mounts
  React.useEffect(() => {
    const id = props.match.params.id;
    if (props.details.state === FetchState.NotFetched) {
      setDetailsValid(false);
      props.onLoadDetail(id);
    } else if (props.details.state === FetchState.Success) {
      if (props.details.data.id !== id) {
        setDetailsValid(false);
        props.onLoadDetail(id);
      }
    }
    if (props.details.state === FetchState.Success && props.details.data) {
      setDetailsValid(true);
      setDetails(props.details.data);
    }
  }, [props.details.state, props.match.params.id, props.details?.data.id]);

  return (
    <ContactpointNavigation navigations={props.navigations} selected={ContactpointNavigationPage.Edit}>
      <UnsavedChangesBar show={hasUnsavedChanges} />
      <PageHeader
        title={tContactpoint('title')}
        subTitle={tContactpoint('detailTitle').replace('{{name}}', details.name || '')}
        explainText={tContactpoint('newContactpointPageExplanation')}
      />

      {isEnabling && <EnablingMessage entity={tContactpoint('contactpoint')} />}
      {isDisabling && <DisablingMessage entity={tContactpoint('contactpoint')} />}
      {isDeleting && <DeletingMessage entity={tContactpoint('contactpoint')} />}
      {props.updater.state === FetchState.Busy && !isError && <SavingMessage entity={tContactpoint('contactpoint')} />}

      <ContactpointForm
        data={details}
        loading={isLoading}
        disabled={!details.active} // Disable all form inputs when contactpoint is not active
        onUpdate={(details, valid) => {
          setDetails(details);
          setDetailsValid(valid);
        }}
        onDirtyChange={() => setHasUnsavedChanges(true)}
        actions={
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            {details.active ? (
              // When contactpoint is active, show save and disable buttons
              <>
                <SaveButton disabled={!detailsValid || isLoading} onClick={() => props.onEdit(details)} />
                <DisableButton disabled={isLoading} onClick={() => handleDisable(details.id)} />
              </>
            ) : (
              // When contactpoint is inactive, show enable button
              <EnableButton disabled={isLoading} onClick={() => handleEnable(details.id)} />
            )}
            <RefreshButton onClick={() => props.onLoadDetail(details.id)} disabled={props.details.state === FetchState.Busy} />
            <DeleteButton 
              disabled={isLoading} 
              onClick={() => props.onDelete(details.id)} 
              deleteWarningMessage={tContactpoint('deleteWarning').replace('{{name}}', details.name || '')} 
            />
          </div>
        }
      />

      {isError && <ErrorMessage 
        message={tCommon('errorLoadingData')} 
        title={tCommon('errorLoadingDataTitle')} 
        errorDetails={props.details.errorMessage} 
        options={[{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} 
      />}

      {props.updater.state === FetchState.Error && !isError && 
        <ErrorMessage 
          title={tCommon('savingErrorTitle')} 
          message={tCommon('savingErrorXMessage', {x: props.updater.errorMessage})} 
          canClose 
          options={[]} 
          errorDetails={JSON.stringify(details)} 
        />
      }
      
      {hasDeleteError && 
        <ErrorMessage 
          title={tCommon('deletingErrorTitle')} 
          message={tCommon('deletingErrorXMessage', {x: props.deleter.errorMessage})} 
          canClose 
          options={[]} 
          errorDetails={props.deleter.errorMessage} 
        />
      }
    </ContactpointNavigation>
  );
}

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(ContactpointEdit);
